import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../../utils/api";
import BulkFileUpload from "../../Scope1/FugitiveEmmissions/BulkFileUpload";
import AddLeasedAssets from "../../Masters/Scope3/LeasedAssets/AddLeasedAssets";

const EXCEL_ASSET_ID_KEY = "Asset ID";
/** Dropdown selection only — separate from excel-entered Asset ID (same pattern as Natural Gas selectedEquipment). */
const SELECTED_ASSET_KEY = "selectedAssetId";

const getEnteredAssetId = (row) =>
  row?.assetId ||
  row?.assetID ||
  row?.equipment ||
  row?.leasedAssetId ||
  row?.["Asset ID"] ||
  row?.["AssetID"] ||
  row?.["assetId"] ||
  row?.["assetID"] ||
  "";

const getMatchedFlag = (row) => {
  if (!row) return false;
  if (typeof row.equipmentMatched === "boolean") return row.equipmentMatched;
  if (typeof row.assetMatched === "boolean") return row.assetMatched;
  if (typeof row.assetIdMatched === "boolean") return row.assetIdMatched;
  if (typeof row.assetIDMatched === "boolean") return row.assetIDMatched;
  if (typeof row.leasedAssetMatched === "boolean") return row.leasedAssetMatched;
  if (typeof row.matchedEquipment === "boolean") return row.matchedEquipment;
  if (typeof row.matched === "boolean") return row.matched;
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  return matchedKey ? Boolean(row[matchedKey]) : false;
};

const setMatchedFlag = (row, next) => {
  if (!row) return row;
  if (typeof row.equipmentMatched === "boolean")
    return { ...row, equipmentMatched: next };
  if (typeof row.assetMatched === "boolean") return { ...row, assetMatched: next };
  if (typeof row.assetIdMatched === "boolean") return { ...row, assetIdMatched: next };
  if (typeof row.assetIDMatched === "boolean") return { ...row, assetIDMatched: next };
  if (typeof row.leasedAssetMatched === "boolean")
    return { ...row, leasedAssetMatched: next };
  if (typeof row.matchedEquipment === "boolean")
    return { ...row, matchedEquipment: next };
  if (typeof row.matched === "boolean") return { ...row, matched: next };
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  if (matchedKey) return { ...row, [matchedKey]: next };
  return { ...row, equipmentMatched: next };
};

const BULK_UPLOAD_JSON_SNACK_DURATION = 5000;

const BulkUploadReview = ({
  matchData = [],
  setMatchData,
  bulkUploadTable,
  onClose,
  onConfirm,
  formDataBulkUpload = [],
  setFormDataBulkUpload,
  setsnackOpen,
  leasedType = "upstream",
  bulkUploadUrl,
}) => {
  const [masterAssets, setMasterAssets] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);

  // Add-new master drawer (reuse Masters form)
  const [addRowIndex, setAddRowIndex] = useState(null);
  const [addMasterElement, setAddMasterElement] = useState(null);
  const [pendingCreatedAssetId, setPendingCreatedAssetId] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  const masterFormInputData = useMemo(
    () => [
      { fieldName: "assetId", title: "Asset ID", placeholder: "e.g. LA-001" },
      {
        fieldName: "typeOfAsset",
        title: "Type of Asset",
        placeholder: "Tap to Select",
        type: "select",
      },
      { fieldName: "leaseStartDate", title: "Lease start date" },
      { fieldName: "leaseEndDate", title: "Lease end date" },
      { fieldName: "assetSize", title: "Asset size", placeholder: "Numeric size" },
      { fieldName: "assetSizeUnit", title: "Asset size unit" },
      { fieldName: "locationCountry", title: "Country", placeholder: "Tap to Select", type: "select" },
    ],
    []
  );

  // Document upload state (same UX as other bulk review screens)
  const [finalJson, setFinalJson] = useState(formDataBulkUpload);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] =
    useState(false);
  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none"); // none | same | individual
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (Array.isArray(formDataBulkUpload)) {
      setFinalJson(formDataBulkUpload);
    }
  }, [formDataBulkUpload]);

  useEffect(() => {
    const fetchMasters = async () => {
      setLoadingMasters(true);
      try {
        const queryParams = new URLSearchParams({
          leasedType,
          limit: "1000",
          page: "1",
        }).toString();
        const res = await get(`/master/leasedAssets?${queryParams}`);
        setMasterAssets(res?.data?.data || []);
      } catch (e) {
        setMasterAssets([]);
      } finally {
        setLoadingMasters(false);
      }
    };

    if (bulkUploadTable) fetchMasters();
  }, [bulkUploadTable, leasedType]);

  const assetIdOptions = useMemo(() => {
    const ids = (masterAssets || [])
      .map((a) => a?.assetId)
      .filter((v) => typeof v === "string" && v.trim().length > 0);
    return Array.from(new Set(ids)).sort((a, b) => a.localeCompare(b));
  }, [masterAssets]);

  const getSelectedAssetIdForIndex = (index) => {
    const row = formDataBulkUpload?.[index];
    return String(row?.[SELECTED_ASSET_KEY] ?? "");
  };

  const hasValidMasterSelection = (index) => {
    const selected = getSelectedAssetIdForIndex(index);
    return Boolean(selected) && selected !== "add_new";
  };

  const setSelectedAssetIdForIndex = (index, nextValue) => {
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = arr[index] || {};
      const next = {
        ...existing,
        [SELECTED_ASSET_KEY]: nextValue,
      };
      if (nextValue && nextValue !== "add_new") {
        next[EXCEL_ASSET_ID_KEY] = nextValue;
      } else if (nextValue === "") {
        const entered = getEnteredAssetId(matchData?.[index]);
        if (entered) next[EXCEL_ASSET_ID_KEY] = entered;
      }
      arr[index] = next;
      return arr;
    });
  };

  const disableConfirm = useMemo(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return true;
    return matchData.some((row, idx) => {
      const matched = getMatchedFlag(row);
      if (matched) return false;
      return !hasValidMasterSelection(idx);
    });
  }, [matchData, formDataBulkUpload]);

  const prepareAddMasterElement = (index) => {
    const entered = String(getEnteredAssetId(matchData?.[index]) || "").trim();
    setAddRowIndex(index);
    setPendingCreatedAssetId(entered);
    setAddMasterElement({
      assetId: entered,
      locationCity: "",
      locationCountry: "",
      typeOfAsset: "",
      leaseStartDate: "",
      leaseEndDate: "",
      assetSize: "",
      assetSizeUnit: "",
    });
  };

  const refreshMasterAssetsAndMarkMatched = async () => {
    // Called after master create succeeds (AddLeasedAssets already showed toast)
    try {
      const queryParams = new URLSearchParams({
        leasedType,
        limit: "1000",
        page: "1",
      }).toString();
      const res = await get(`/master/leasedAssets?${queryParams}`);
      const list = res?.data?.data || [];
      setMasterAssets(list);

      if (typeof addRowIndex === "number" && pendingCreatedAssetId) {
        setSelectedAssetIdForIndex(addRowIndex, pendingCreatedAssetId);
        setMatchData((prev) =>
          (prev || []).map((r, i) =>
            i === addRowIndex ? setMatchedFlag(r, true) : r
          )
        );
      }
    } finally {
      setExpandedRows((prev) => ({ ...prev, ...(addRowIndex !== null ? { [addRowIndex]: false } : {}) }));
      setAddRowIndex(null);
      setPendingCreatedAssetId("");
      setAddMasterElement(null);
    }
  };

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
      setFinalJson((prev) =>
        Array.isArray(prev)
          ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
          : prev
      );
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    setCommonDocument(files);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({
          ...entry,
          "Document (optional)": files,
        }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry) => ({
        ...entry,
        "Document (optional)": files,
      }));
      setFinalJson(updated);
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };
  const handleCommonDocumentClear = () => {
    setCommonDocument(null);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({
          ...entry,
          "Document (optional)": null,
        }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry) => ({
        ...entry,
        "Document (optional)": null,
      }));
      setFinalJson(updated);
    }
  };
  const handleIndividualDocumentChange = (index, files) => {
    setIndividualDocuments((prev) => ({ ...prev, [index]: files }));
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": files } : entry
        )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": files } : entry
      );
      setFinalJson(updated);
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };
  const handleIndividualDocumentClear = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": null } : entry
        )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": null } : entry
      );
      setFinalJson(updated);
    }
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push(
        "Please select a document upload type when document upload is confirmed."
      );
    }
    if (documentUploadConfirmed && documentUploadType !== "none") {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
      }
    }
    return validationErrors;
  };

  const performUpload = async () => {
    try {
      setUploading(true);
      const dataToUse =
        Array.isArray(finalJson) && finalJson.length > 0
          ? finalJson
          : formDataBulkUpload;
      const uploadData = {
        excelData: dataToUse,
        documentUploadType,
        commonDocument,
        individualDocuments,
      };
      const res = await post(bulkUploadUrl, uploadData);

      const inserted = Number(res?.data?.inserted ?? 0);
      const skipped = Number(res?.data?.skipped ?? 0);

      if (inserted === 0) {
        setsnackOpen?.({
          open: true,
          message:
            skipped > 0
              ? `No data inserted. ${skipped} row${skipped > 1 ? "s were" : " was"} skipped. Please review and try again.`
              : res?.data?.message ||
                "No data inserted. Please review and try again.",
          severity: "error",
          autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
        });
        return;
      }

      const responseData = res?.data ?? {};
      const skippedCount = Number(responseData?.skipped ?? skipped ?? 0);
      const apiMessage = responseData?.message;
      const successMessage = "Data uploaded successfully";
      setsnackOpen?.({
        open: true,
        message:
          skippedCount > 0
            ? apiMessage ||
              `${inserted} entr${inserted === 1 ? "y" : "ies"} inserted. ${skippedCount} row${skippedCount === 1 ? "" : "s"} skipped.`
            : successMessage,
        severity: skippedCount > 0 ? "warning" : "success",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });

      onConfirm?.(responseData);
      onClose?.();
    } catch (e) {
      const errMessage = e?.response?.data?.details
        ?.map((el) => el?.error || el?.reason || el?.msg)
        ?.filter(Boolean)
        ?.join(", ");
      setsnackOpen?.({
        open: true,
        message:
          errMessage ?? e?.response?.data?.message ?? "Upload failed",
        severity: "error",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }
    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }
    await performUpload();
  };

  if (!bulkUploadTable) return null;

  const reviewRows =
    Array.isArray(matchData) && matchData.length > 0
      ? matchData
      : Array.isArray(formDataBulkUpload)
        ? formDataBulkUpload
        : [];

  if (reviewRows.length === 0) return null;

  return (
    <>
      <Dialog
        open={bulkUploadTable}
        onClose={onClose}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            margin: "16px",
            maxHeight: "calc(100% - 32px)",
            zIndex: 1000,
          },
        }}
      >
        <DialogTitle>Review Bulk Upload Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review the matched and unmatched leased assets data. Add unmatched
            items individually.
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Data Entered
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Master Data
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                    Action
                  </TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                      Document
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Asset ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Asset ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {reviewRows.map((row, index) => {
                  const entered = getEnteredAssetId(row);
                  const matched = getMatchedFlag(row);
                  const selectedIsAddNew =
                    getSelectedAssetIdForIndex(index) === "add_new";
                  return (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>{entered || "-"}</TableCell>
                        <TableCell>
                          {matched ? (
                            <Chip label="Matched" color="success" size="small" />
                          ) : (
                            <FormControl
                              size="small"
                              sx={{ minWidth: "10.5rem", maxWidth: "10.5rem" }}
                            >
                              <Select
                                value={getSelectedAssetIdForIndex(index) || ""}
                                onChange={(e) => {
                                  const nextValue = e.target.value;
                                  if (nextValue === "add_new") {
                                    setSelectedAssetIdForIndex(index, "add_new");
                                    prepareAddMasterElement(index);
                                    setExpandedRows((prev) => ({
                                      ...prev,
                                      [index]: true,
                                    }));
                                    return;
                                  }
                                  setSelectedAssetIdForIndex(index, nextValue);
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [index]: false,
                                  }));
                                  setMatchData((prev) =>
                                    (prev || []).map((r, i) =>
                                      i === index
                                        ? setMatchedFlag(r, Boolean(nextValue))
                                        : r
                                    )
                                  );
                                }}
                                fullWidth
                                displayEmpty
                                disabled={loadingMasters}
                              >
                                <MenuItem value="" disabled>
                                  Tap to Select
                                </MenuItem>
                                <MenuItem value="add_new">
                                  Add New Leased Asset +
                                </MenuItem>
                                {assetIdOptions.map((id) => (
                                  <MenuItem key={id} value={id}>
                                    {id}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell>
                          {!matched &&
                            (selectedIsAddNew || expandedRows[index] ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  const willClose = expandedRows[index];
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [index]: !prev[index],
                                  }));
                                  if (!willClose) {
                                    prepareAddMasterElement(index);
                                  }
                                }}
                                sx={{ minWidth: "80px" }}
                              >
                                {expandedRows[index] ? "Close" : "Open"}
                              </Button>
                            ) : (
                              <Chip
                                label={
                                  hasValidMasterSelection(index)
                                    ? "Selected"
                                    : "Unmatched"
                                }
                                color={
                                  hasValidMasterSelection(index)
                                    ? "info"
                                    : "warning"
                                }
                                size="small"
                              />
                            ))}
                        </TableCell>
                        {documentUploadConfirmed &&
                          documentUploadType === "individual" && (
                            <TableCell>
                              <BulkFileUpload
                                handleChange={handleIndividualDocumentChange}
                                handleClear={handleIndividualDocumentClear}
                                filesList={
                                  individualDocuments[index] ||
                                  formDataBulkUpload?.[index]?.["Document (optional)"] ||
                                  finalJson?.[index]?.["Document (optional)"] ||
                                  []
                                }
                                index={index}
                              />
                            </TableCell>
                          )}
                      </TableRow>

                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={
                            documentUploadConfirmed &&
                              documentUploadType === "individual"
                              ? 4
                              : 3
                          }
                        >
                          <Collapse
                            in={Boolean(expandedRows[index])}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ mt: 2 }}>
                              <AddLeasedAssets
                                id={""}
                                addData={Boolean(expandedRows[index])}
                                setAddData={(next) => {
                                  if (!next) {
                                    setExpandedRows((prev) => ({
                                      ...prev,
                                      [index]: false,
                                    }));
                                  }
                                }}
                                data={masterAssets}
                                element={
                                  addRowIndex === index
                                    ? addMasterElement
                                    : null
                                }
                                addUrl={"/master/leasedAssets"}
                                updateUrl={"/master/leasedAssets"}
                                fetchData={refreshMasterAssetsAndMarkMatched}
                                setsnackOpen={setsnackOpen}
                                formInputData={masterFormInputData}
                                leasedType={leasedType}
                                embedded={true}
                                embeddedTitle={`Add Leased Asset: ${entered || ""}`}
                                embeddedFieldsContainerSx={{
                                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                  columnGap: 2,
                                  rowGap: 1,
                                }}
                                leaseEndStrictlyAfterStart
                                assetIdReadOnly
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Document Upload
            </Typography>
            <Box sx={{ width: "100%" }}>
              <FormControl
                component="fieldset"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                  m: 0,
                }}
              >
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, color: "text.primary", fontSize: "0.875rem" }}
                >
                  Do you want to upload document?
                </FormLabel>
                <RadioGroup
                  value={documentUploadConfirmed ? "yes" : "no"}
                  onChange={(e) =>
                    handleDocumentUploadConfirmation(e.target.value === "yes")
                  }
                  row
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Box>

            {documentUploadConfirmed && (
              <Box
                sx={{
                  width: "100%",
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <FormControl
                  component="fieldset"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    m: 0,
                  }}
                >
                  <FormLabel
                    component="legend"
                    sx={{ mb: 1, color: "text.primary", fontSize: "0.875rem" }}
                  >
                    Document Upload Type
                  </FormLabel>
                  <RadioGroup
                    value={documentUploadType}
                    onChange={handleDocumentUploadTypeChange}
                    row
                    sx={{ flexWrap: "wrap", gap: 0.5 }}
                  >
                    <FormControlLabel
                      value="same"
                      control={<Radio />}
                      label="Same File for All Records"
                    />
                    <FormControlLabel
                      value="individual"
                      control={<Radio />}
                      label="Individual File for Each Record"
                    />
                  </RadioGroup>
                </FormControl>

                {documentUploadType === "same" && (
                  <Box sx={{ mt: 2, width: "100%" }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Upload Common Document:
                    </Typography>
                    <BulkFileUpload
                      handleChange={handleCommonDocumentChange}
                      handleClear={handleCommonDocumentClear}
                      filesList={commonDocument}
                    />
                  </Box>
                )}

                {documentUploadType === "individual" && (
                  <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    Upload individual documents for each record. Documents will be
                    added as a new column in the table above.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#ffebee",
                borderRadius: 1,
                border: "1px solid #f44336",
              }}
            >
              <Typography
                variant="h6"
                color="error"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Error:
              </Typography>
              {uploadValidationErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  color="error"
                  sx={{ mb: 0.5 }}
                >
                  • {error}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="primary"
            disabled={disableConfirm || uploading}
          >
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showNoDocumentDialog}
        onClose={() => setShowNoDocumentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">No Documents Uploaded</Typography>
            <IconButton onClick={() => setShowNoDocumentDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have selected to upload documents but haven't uploaded any files.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to proceed without uploading documents?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can still upload documents later if needed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={() => setShowNoDocumentDialog(false)}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
          >
            Upload Documents
          </Button>
          <Button
            onClick={() => {
              setShowNoDocumentDialog(false);
              performUpload();
            }}
            variant="contained"
            color="primary"
          >
            Proceed Without Documents
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadReview;

